const TINY_CHANGE_PCT = 0.5;

export function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

export function formatUnavailable(): string {
  return '—';
}

/** Signed percent for KPI cards, e.g. +4.8% / −0.8% / — */
export function formatSignedPercentChange(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return formatUnavailable();
  if (Math.abs(value) < TINY_CHANGE_PCT) return '0%';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

export function changeDirectionWord(pct: number | null | undefined): string {
  if (!isFiniteNumber(pct)) return 'an unavailable change';
  if (Math.abs(pct) < TINY_CHANGE_PCT) return 'little change';
  return pct > 0 ? 'an increase' : 'a decrease';
}

export function changeDirectionShort(pct: number | null | undefined): string {
  if (!isFiniteNumber(pct)) return 'unchanged';
  if (Math.abs(pct) < TINY_CHANGE_PCT) return 'about flat';
  return pct > 0 ? 'higher' : 'lower';
}

export function formatPercentMagnitude(pct: number | null | undefined): string {
  if (!isFiniteNumber(pct)) return formatUnavailable();
  return `${Math.abs(pct).toFixed(1)}%`;
}

export function compareToReference(
  local: number | null | undefined,
  reference: number | null | undefined
): { difference: number | null; percentDifference: number | null; relation: 'above' | 'below' | 'about' | 'unavailable' } {
  if (!isFiniteNumber(local) || !isFiniteNumber(reference) || reference === 0) {
    return { difference: null, percentDifference: null, relation: 'unavailable' };
  }
  const difference = local - reference;
  const percentDifference = (difference / reference) * 100;
  if (Math.abs(percentDifference) < TINY_CHANGE_PCT) {
    return { difference, percentDifference, relation: 'about' };
  }
  return { difference, percentDifference, relation: local > reference ? 'above' : 'below' };
}

export function formatComparatorPhrase(
  relation: 'above' | 'below' | 'about' | 'unavailable',
  comparatorLabel = 'California'
): string {
  if (relation === 'unavailable') return `compared with ${comparatorLabel}`;
  if (relation === 'about') return `about the same as ${comparatorLabel}`;
  if (relation === 'above') return `above ${comparatorLabel}`;
  return `below ${comparatorLabel}`;
}

export function sectorSharePercent(part: number, total: number): number | null {
  if (!isFiniteNumber(part) || !isFiniteNumber(total) || total <= 0) return null;
  return Math.round((part / total) * 1000) / 10;
}
